import { get } from 'lodash';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Text, FormElements, SVGIcon } from '@custom-components';
import { TablePagination, TableBody, TableRow, TableHead } from '@mui/material';
import { TableWrapper, StyledCell, StyledTable, HeaderRow, StyledTableRow } from './SelectTable.styles';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';

class ExpandTable extends Component {
  constructor(props) {
    super(props);
    const { children, initialGuestDays } = this.props;
    const selected = {};

    children.forEach((child) => {
      selected[child.id] = initialGuestDays[child.id] || [];
    });

    this.state = {
      selected,
      expanded: null,
      subPage: 0,
      subPageSize: 10
    };

    this.renderBody = this.renderBody.bind(this);
    this.renderHead = this.renderHead.bind(this);
    this.renderSubHead = this.renderSubHead.bind(this);
    this.renderSubBody = this.renderSubBody.bind(this);
  }

  switchExpand = (id) => {
    const { expanded } = this.state;
    if (id === expanded) {
      this.setState({ expanded: null, subPage: 0 });
    } else {
      this.setState({ expanded: id, subPage: 0 });
    }
  };

  isSelected = (id, subChildren) => {
    const { selected } = this.state;
    return selected[id] ? selected[id].length === subChildren.length && subChildren.length !== 0 : false;
  };

  isSubSelected = (id, subId) => {
    const { selected } = this.state;
    return selected[id] ? selected[id].indexOf(subId) !== -1 : false;
  };

  // select all of everything
  handleSelectAllClick = (event) => {
    const { children, onHandleSelect } = this.props;
    const newSelected = {};
    const setSelected = [];
    if (event.target.checked) {
      children.forEach((child) => {
        newSelected[child.id] = [];
        child.subChildren.forEach((subChild) => {
          newSelected[child.id].push(subChild.id);
          setSelected.push(subChild.id);
        });
      });
    } else {
      children.forEach((child) => {
        newSelected[child.id] = [];
      });
    }
    onHandleSelect(setSelected, newSelected);
    this.setState({ selected: newSelected });
  };

  // select main row, sub rows under it
  handleSelectClick = (event, id, subChildren) => {
    const { onHandleSelect } = this.props;
    const { selected } = this.state;
    const newSelected = { ...selected };
    if (event.target.checked) {
      newSelected[id] = subChildren.map((child) => child.id);
    } else {
      newSelected[id] = [];
    }
    const setSelected = [];
    Object.keys(newSelected).forEach((key) => {
      setSelected.push(...newSelected[key]);
    });
    onHandleSelect(setSelected, newSelected);
    this.setState({ selected: newSelected });
  };

  handleSubSelectClick = (event, id, subId) => {
    const { onHandleSelect } = this.props;
    const { selected } = this.state;
    const newSelected = { ...selected };
    const selectedIndex = newSelected[id].indexOf(subId);
    if (selectedIndex === -1) {
      newSelected[id] = selected[id].concat(subId);
    } else if (selectedIndex === 0) {
      newSelected[id] = selected[id].slice(1);
    } else if (selectedIndex === selected.length - 1) {
      newSelected[id] = selected[id].slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected[id].splice(selectedIndex, 1);
    }
    const setSelected = [];
    Object.keys(newSelected).forEach((key) => {
      setSelected.push(...newSelected[key]);
    });
    onHandleSelect(setSelected, newSelected);
    this.setState({ selected: newSelected });
  };

  isAllSelected = () => {
    const { children } = this.props;
    const { selected } = this.state;
    let allSelected = true;

    children.forEach((child) => {
      if (selected[child.id]) {
        if (child.subChildren.length !== selected[child.id].length) {
          allSelected = false;
        }
      } else {
        allSelected = false;
      }
    });

    return allSelected;
  };

  isAnySelected = () => {
    const { selected } = this.state;
    let anySelected = false;
    Object.keys(selected).forEach((key) => {
      if (selected[key].length !== 0) {
        anySelected = true;
      }
    });
    return anySelected;
  };

  handleSubChangePage = (event, newPage) => {
    this.setState({ subPage: newPage });
  };

  handleSubChangeRowsPerPage = (event) => {
    const newSize = event.target.value;
    this.setState({ subPageSize: newSize });
  };

  renderHead() {
    const { rows, headerCellsConfig } = this.props;
    const isAllSelected = this.isAllSelected();
    const isAnySelected = isAllSelected ? false : this.isAnySelected();
    return rows ? (
      <TableHead>
        <HeaderRow>
          <StyledCell padding="checkbox" style={{ width: '40px' }}>
            <FormElements.Checkbox
              indeterminate={isAnySelected}
              checked={isAllSelected}
              onChange={this.handleSelectAllClick}
            />
          </StyledCell>
          {rows.map((row) => {
            const { id, label } = row;
            const cellConfigProps = get(headerCellsConfig, `${id}`, {});
            return (
              <StyledCell key={id} {...cellConfigProps}>
                <Text.CopyMini noMargins>{label}</Text.CopyMini>
              </StyledCell>
            );
          })}
        </HeaderRow>
      </TableHead>
    ) : undefined;
  }

  renderSubHead() {
    const { subRows, headerCellsConfig } = this.props;
    return subRows ? (
      <TableHead>
        <HeaderRow style={{ height: '24px' }}>
          <StyledCell />
          {subRows.map((row) => {
            const { id, label } = row;
            const cellConfigProps = get(headerCellsConfig, `${id}`, {});
            return (
              <StyledCell key={id} {...cellConfigProps}>
                <Text.CopyMini noMargins>{label}</Text.CopyMini>
              </StyledCell>
            );
          })}
        </HeaderRow>
      </TableHead>
    ) : undefined;
  }

  renderBody() {
    const { children, contentCellsConfig, subHeader } = this.props;
    const { selected, expanded } = this.state;
    return children ? (
      <TableBody>
        {children.map((child) => {
          const isSelected = this.isSelected(child.id, child.subChildren);
          const isExpanded = expanded === child.id;
          return (
            <Fragment key={child.id}>
              <StyledTableRow selected={isSelected} expanded={isExpanded.toString()}>
                <StyledCell padding="checkbox">
                  <FormElements.Checkbox
                    indeterminate={isSelected || !selected[child.id] ? false : selected[child.id].length !== 0}
                    checked={isSelected}
                    onChange={(e) => this.handleSelectClick(e, child.id, child.subChildren)}
                  />
                </StyledCell>
                {Object.entries(child).map(([key, item]) => {
                  if (key === 'id' || key === 'subChildren' || key === 'roomTypes') return null;
                  const cellConfigProps = get(contentCellsConfig, `${key}`, {});
                  return (
                    <StyledCell key={key} {...cellConfigProps}>
                      {item}
                    </StyledCell>
                  );
                })}
                <StyledCell key="expand" {...get(contentCellsConfig, 'expand', {})}>
                  <button
                    aria-labelledby="expand row"
                    onClick={() => {
                      this.switchExpand(child.id);
                    }}
                    type="button"
                  >
                    <SVGIcon selectedIcon="arrow" rotate={isExpanded ? 180 : 0} />
                  </button>
                </StyledCell>
              </StyledTableRow>
              {isExpanded && (
                <>
                  {subHeader && subHeader(child, selected[child.id])}
                  <TableRow>
                    <StyledCell colSpan={Object.entries(child).length + 1}>
                      <StyledTable minTableWidth="100%">
                        {this.renderSubHead()}
                        {this.renderSubBody(child.id, child.subChildren)}
                      </StyledTable>
                    </StyledCell>
                  </TableRow>
                </>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    ) : undefined;
  }

  renderSubBody(id, subChildren) {
    const { contentCellsConfig } = this.props;
    const { subPage, subPageSize } = this.state;
    return subChildren ? (
      <TableBody>
        {subChildren.slice(subPage * subPageSize, subPage * subPageSize + subPageSize).map((child) => {
          const isSelected = this.isSubSelected(id, child.id);
          return (
            <TableRow key={child.id} selected={isSelected}>
              <StyledCell padding="checkbox">
                <FormElements.Checkbox
                  checked={isSelected}
                  onChange={(e) => this.handleSubSelectClick(e, id, child.id)}
                />
              </StyledCell>
              {Object.entries(child).map(([key, item]) => {
                if (key === 'id') return null;
                const cellConfigProps = get(contentCellsConfig, `${key}`, {});
                return (
                  <StyledCell key={key} {...cellConfigProps}>
                    {item}
                  </StyledCell>
                );
              })}
            </TableRow>
          );
        })}
        <TableRow>
          <TablePagination
            count={subChildren.length || 0}
            page={subPage}
            rowsPerPage={subPageSize}
            rowsPerPageOptions={[5, 10, 20]}
            onPageChange={this.handleSubChangePage}
            onRowsPerPageChange={this.handleSubChangeRowsPerPage}
          />
        </TableRow>
      </TableBody>
    ) : undefined;
  }

  render() {
    const { minTableWidth } = this.props;
    return (
      <TableWrapper>
        <StyledTable minTableWidth={minTableWidth}>
          {this.renderHead()}
          {this.renderBody()}
        </StyledTable>
      </TableWrapper>
    );
  }
}

ExpandTable.propTypes = {
  /** Array of content objects to display in table */
  children: ReactChildrenPropType,
  /** Object with keys matching row object IDs containing @MUITableCell properties and valid td attributes */
  contentCellsConfig: PropTypes.shape({}),
  /** Object with keys matching row object IDs containing @MUITableCell properties and valid td attributes */
  headerCellsConfig: PropTypes.shape({}),
  /** Min width of the table passed via string (900px default) */
  minTableWidth: PropTypes.string,
  /** Array of label objects to display at the top of the table */
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      width: PropTypes.string
    })
  ).isRequired,
  subRows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      width: PropTypes.string
    })
  ).isRequired,
  onHandleSelect: PropTypes.func, // ([ids] => {})
  initialGuestDays: PropTypes.shape({}),
  subHeader: PropTypes.func
};

ExpandTable.defaultProps = {
  children: undefined,
  contentCellsConfig: {},
  headerCellsConfig: {},
  minTableWidth: '900px',
  onHandleSelect: () => {},
  initialGuestDays: {},
  subHeader: null
};

export default ExpandTable;
